import {
  createContext,
  useContext,
  useLayoutEffect,
  type ReactNode,
} from 'react';

/**
 * What the dark band says about the reader's standing, when it is not a member's
 * (spec 196 US1): "View only" or "Organization admin".
 *
 * A context rather than a prop on every page: the band is drawn by each page's own
 * `PageHeader`, a dozen of them, and the standing is the workspace's, known once by
 * the workspace layout. The layout provides it and every band shows it, including a
 * page added later. `null` — nothing to say — everywhere else, so the personal
 * settings pages and the console never carry it.
 *
 * Shared code names no feature: the layout decides what goes here.
 */
export const BandStandingContext = createContext<ReactNode>(null);

/**
 * Whether a band on this page is saying it, so the top bar need not say it again.
 *
 * The standing used to show twice: a chip in the top bar and the pill in the band.
 * The band is read first, so it keeps it. Each band showing the pill registers
 * while it is mounted, and the layout counts them: the top-bar chip shows only
 * while the count is zero. A count rather than a flag, so two bands on screen at
 * once (one leaving, one arriving) never hand the chip back early.
 */
export interface BandPresence {
  /** Registers a band showing the standing; returns the unregister. */
  register: () => () => void;
  /** A band on this page is showing it. */
  shown: boolean;
}

export const BandPresenceContext = createContext<BandPresence>({
  register: () => () => {},
  shown: false,
});

/**
 * Called by a band while it shows the standing. A layout effect, so the count is
 * up before the first paint and the chip is never drawn beside the pill.
 */
export function useRegisterBandStanding(showing: boolean): void {
  const { register } = useContext(BandPresenceContext);
  useLayoutEffect(
    () => (showing ? register() : undefined),
    [showing, register],
  );
}

/** Whether the top bar should leave the standing to the band. */
export function useBandShowsStanding(): boolean {
  return useContext(BandPresenceContext).shown;
}
